<template>
  <div class="ui-page" :class="{ 'ui-page--with-assistant': aiOpen }" tabindex="-1">
    <LeadFlowListFilters
      :value="filters"
      :countries="countryOptions"
      :nurture-reasons="nurtureReasonOptions"
      :adv-sources="advSourceOptions"
      :tour-operators="tourOperatorOptions"
      :domains="domainOptions"
      :managers="managerOptions"
      :managers-loading="managersLoading"
      :managers-loading-more="managersLoadingMore"
      :manager-has-more="managerHasMore"
      :manager-error="managerError"
      :show-manager="scope.can_filter_by_manager"
      :show-advanced-filters="scope.can_use_advanced_filters"
      @change="onFilterChange"
      @clear="clearFilters"
      @manager-open="onManagerOpen"
      @manager-search="onManagerSearch"
      @manager-load-more="loadMoreManagers"
      @manager-retry="retryManagers"
    >
      <template v-if="canCreateDirectLead" #create>
        <app-button design="new" @click.native="$refs.directLeadModal.open()">New Direct Lead</app-button>
      </template>
      <template #selection>
        <LeadFlowListSummary
          :summary="summary"
          :loading="summaryLoading"
          :error="summaryError"
          :selected-stages="filters.stages"
          @retry="loadSummary"
          @toggle-stage="toggleStageFilter"
        />
      </template>
      <template #actions>
        <AppIconButton :label="exportLoading ? 'Preparing export…' : 'Export .xlsx'" tooltip-placement="left" :disabled="exportLoading" :aria-busy="String(exportLoading)" @click="exportLeads">
          <i class="pi pi-file-excel" aria-hidden="true" />
        </AppIconButton>
      </template>
    </LeadFlowListFilters>

    <AppContentSkeleton v-if="listLoading" class="lead-flow-leads__table" label="Loading leads" :columns="tableColumns" min-width="1200px" />
    <AppErrorState v-else-if="listError" :message="listError" @retry="loadLeads" />
    <app-no-results v-else-if="!leads.length" design="new" title="No leads found" text="Try changing or clearing the filters." />
    <AppCard v-else design="new" variant="table" header-density="compact">
      <AppDataTable
        class="lead-flow-leads__table"
        design="new"
        density="dense"
        :rows="leads"
        :columns="tableColumns"
        :loading="listLoading"
        min-width="1200px"
        clickable
        @row-click="openLead"
      >
        <template #cell-stage="{ row }">
          <AppStatusBadge
            v-if="row.stage"
            design="new"
            compact
            :color="row.stage.color || ''"
            :label="row.stage.name"
          />
          <span v-else>—</span>
        </template>

        <template #cell-reason="{ row }">
          <AppTableText :text="row.reason && row.reason.name" />
        </template>

        <template #cell-lead="{ row }">
          <div class="ui-cell-inline">
            <LeadManagementGuidanceIcon :status="row.management_guidance_status" :comment="row.management_guidance_comment" />
            <span
              v-if="countryCode(row.country)"
              v-tooltip="newTooltip(row.country && row.country.name)"
              class="ui-tooltip-target"
              :tabindex="row.country && row.country.name ? 0 : null"
              :class="`fflag ff-sm fflag-${countryCode(row.country)}`"
              :aria-label="countryName(row.country)"
            ></span>
            <span v-else class="ui-cell-country-empty" aria-hidden="true"></span>
            <AppTableText :text="row.name" fallback="Unnamed lead" />
          </div>
        </template>

        <template #cell-pax="{ row }">
          <div v-if="hasParty(row.party)" class="ui-cell-party" :aria-label="partyLabel(row.party)">
            <strong>{{ partyTotal(row.party) }}</strong>
            <span
              v-for="group in partyGroups(row.party)"
              :key="group.code"
              v-tooltip="newTooltip(`${group.count} ${group.label}`)"
              class="ui-cell-party-group ui-tooltip-target"
              tabindex="0"
              :aria-label="`${group.count} ${group.label}`"
            >
              <img :src="group.icon" alt="" aria-hidden="true">
              <span>{{ group.count }}</span>
            </span>
          </div>
          <span v-else>—</span>
        </template>

        <template #cell-communication="{ row }">
          <div class="ui-cell-inline ui-cell-inline--indicators" aria-label="Available communication channels">
            <span
              v-for="channel in communicationChannels(row.contact)"
              :key="channel.code"
              v-tooltip="newTooltip(channel.tooltip)"
              class="ui-cell-channel ui-tooltip-target"
              tabindex="0"
              :class="[
                `ui-cell-channel--${channel.code}`,
                {
                  'ui-cell-channel--available': channel.available,
                  'ui-cell-channel--preferred': channel.preferred
                }
              ]"
              :aria-label="channel.tooltip"
            >
              <i v-if="channel.code === 'email'" class="pi pi-envelope" aria-hidden="true" />
              <img v-else :src="channel.icon" alt="" aria-hidden="true">
              <span v-if="channel.preferred" class="ui-cell-preferred" aria-hidden="true">✓</span>
            </span>
          </div>
        </template>

        <template #cell-activities="{ row }">
          <LeadActivityIcons
            v-if="row.activities && row.activities.length"
            design="new"
            icon-set="crm"
            icon-only
            nowrap
            keyboard-tooltips
            :activities="row.activities"
            :max-visible="4"
          />
          <span v-else>—</span>
        </template>

        <template #cell-travel_time="{ row }">
          <AppTableText :text="travelLabel(row.travel)" />
        </template>

        <template #cell-manager="{ row }">
          <AppTableUser :name="row.manager && row.manager.name" :photo="row.manager && row.manager.photo" />
        </template>
      </AppDataTable>

    </AppCard>

    <AppPagination
      v-if="!listLoading && !listError && leads.length"
      placement="embedded"
        design="new"
        :page="pagination.page"
        :pages="pagination.pages"
        :count="pagination.count"
        :visible-count="leads.length"
        :page-size="pagination.pageSize"
        :loading="listLoading"
        label="Lead pages"
        @change="changePage"
      />

    <LeadDirectCreateModal v-if="canCreateDirectLead" ref="directLeadModal" @created="loadResults" />
    <LeadFlowModal
      v-if="selectedModalId"
      :key="selectedModalId"
      :lead-id="selectedModalId"
      :ai-open="aiOpen"
      @ask-ai="aiOpen = !aiOpen"
      @board-reconcile="loadLeads"
      @close="closeRouteModal"
    />
    <KnowledgeChatPanel :open="aiOpen" @minimize="aiOpen = false" />
  </div>
</template>

<script>
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppIconButton from '@/components/AppIconButton.vue'
import AppTableText from '@/components/AppTableText.vue'
import AppTableUser from '@/components/AppTableUser.vue'
import AdultIcon from '@/assets/img/adult-icon.svg'
import ChildIcon from '@/assets/img/child-icon.svg'
import TeenagerIcon from '@/assets/img/teenager-icon.svg'
import WhatsAppIcon from '@/assets/img/wa-round-icon.svg'
import KnowledgeChatPanel from '@/components/knowledge-ai/ChatPanel.vue'
import {
  getClientsTourOperators,
  getCountries,
  getLeadsAdvSources,
  getLeadFlowTrackerLeads,
  getLeadFlowTrackerLeadsExport,
  getLeadFlowTrackerLeadsSummary,
  getLeadFlowTrackerManagers
} from '@/http'
import LeadDirectCreateModal from '@/views/crm/task-tracker/LeadDirectCreateModal.vue'
import LeadActivityIcons from '@/views/crm/task-tracker/LeadActivityIcons.vue'
import LeadManagementGuidanceIcon from '@/views/crm/task-tracker/LeadManagementGuidanceIcon.vue'
import LeadFlowListFilters from '@/views/crm/task-tracker/LeadFlowListFilters.vue'
import LeadFlowListSummary from '@/views/crm/task-tracker/LeadFlowListSummary.vue'
import LeadFlowModal from '@/views/crm/task-tracker/LeadFlowModal.vue'
import { formatTravelMonth } from '@/views/crm/task-tracker/leadFlowDate'
import { modalRoute } from '@/views/crm/modalRoute'
import managementGuidancePolling from '@/views/crm/task-tracker/managementGuidancePolling'

export default {
  name: 'LeadFlowLeadsList',
  mixins: [modalRoute('lead'), managementGuidancePolling],
  components: {
    AppContentSkeleton,
    AppErrorState,
    AppIconButton,
    AppTableText,
    AppTableUser,
    LeadDirectCreateModal,
    KnowledgeChatPanel,
    LeadActivityIcons,
    LeadManagementGuidanceIcon,
    LeadFlowListFilters,
    LeadFlowListSummary,
    LeadFlowModal
  },
  data() {
    return {
      aiOpen: false,
      leads: [],
      listLoading: false,
      listError: '',
      listRequestId: 0,
      exportLoading: false,
      searchTimer: null,
      filters: {
        search: '',
        stages: [],
        nurtureReasons: [],
        country: null,
        activity: [],
        manager: null,
        requestDates: [],
        confirmationDates: [],
        travelDates: [],
        advSources: [],
        tourOperators: [],
        domains: []
      },
      summary: null,
      summaryLoading: false,
      summaryError: '',
      summaryRequestId: 0,
      pagination: {
        page: 1,
        pages: 1,
        count: 0,
        pageSize: 40
      },
      countryOptions: [],
      nurtureReasonOptions: [],
      advSourceOptions: [],
      tourOperatorOptions: [],
      domainOptions: [],
      managerOptions: [],
      managersLoading: false,
      managersLoadingMore: false,
      managersLoaded: false,
      managerSearch: '',
      managerNextCursor: null,
      managerHasMore: false,
      managerError: null,
      managerRequestId: 0,
      managerSearchTimer: null,
      scope: {
        mode: 'own',
        can_filter_by_manager: false,
        can_use_advanced_filters: false,
        selected_manager: null
      }
    }
  },
  computed: {
    canCreateDirectLead() {
      return (this.$store.state.userGroups || []).some(item => ['general_manager', 'sales_supervisor'].includes(item.code))
    },
    tableColumns() {
      return [
        { key: 'stage', label: 'Stage', width: '12%' },
        { key: 'reason', label: 'Reason', width: '11%' },
        { key: 'lead', label: 'Lead', width: '18%' },
        { key: 'pax', label: 'Pax', width: '9%' },
        { key: 'communication', label: 'Communication', width: '11%' },
        { key: 'activities', label: 'Activities', width: '148px' },
        { key: 'travel_time', label: 'Travel time', width: '11%' },
        { key: 'manager', label: 'Manager', width: '16%' }
      ]
    }
  },
  watch: {
    '$route.query': {
      deep: true,
      handler(query) {
        const filters = this.filtersFromQuery(query)
        if (this.filterSignature(filters) === this.filterSignature(this.filters)) return
        window.clearTimeout(this.searchTimer)
        this.filters = filters
        this.pagination.page = 1
        this.loadResults()
      }
    }
  },
  created() {
    this.filters = this.filtersFromQuery(this.$route.query)
    this.syncFilterRoute()
    this.loadCountries()
    this.loadFilterOptions()
    this.loadResults()
  },
  beforeDestroy() {
    window.clearTimeout(this.searchTimer)
    window.clearTimeout(this.managerSearchTimer)
    this.listRequestId += 1
    this.summaryRequestId += 1
    this.managerRequestId += 1
  },
  methods: {
    guidanceRows() {
      return this.leads
    },
    loadResults() {
      this.loadLeads()
      this.loadSummary()
    },
    async loadLeads() {
      const requestId = ++this.listRequestId
      this.listLoading = true
      this.listError = ''
      try {
        const { data } = await getLeadFlowTrackerLeads(this.listParams())
        if (requestId !== this.listRequestId) return
        this.scope = data.scope || this.scope
        if (!this.scope.can_filter_by_manager && this.filters.manager) {
          this.filters = { ...this.filters, manager: null }
          this.syncFilterRoute()
          this.pagination.page = 1
          this.loadResults()
          return
        }
        if (!this.scope.can_use_advanced_filters && this.hasAdvancedFilters(this.filters)) {
          this.filters = this.withoutAdvancedFilters(this.filters)
          this.syncFilterRoute()
          this.pagination.page = 1
          this.loadResults()
          return
        }
        if (this.scope.selected_manager) this.addManagerOption(this.scope.selected_manager)
        this.leads = data.results || []
        this.pagination = {
          page: data.page || 1,
          pages: data.pages || 1,
          count: data.count || 0,
          pageSize: data.page_size || this.pagination.pageSize
        }
      } catch (error) {
        if (requestId !== this.listRequestId) return
        this.leads = []
        this.listError = this.errorMessage(error, 'Unable to load leads')
      } finally {
        if (requestId === this.listRequestId) this.listLoading = false
      }
    },
    listParams() {
      return {
        ...this.filterParams(),
        page: this.pagination.page,
        page_size: this.pagination.pageSize
      }
    },
    filterParams(filters = this.filters) {
      const params = {}
      const search = (filters.search || '').trim()
      if (search) return { search }
      if (filters.stages.length) params.stages = filters.stages.join(',')
      if ((filters.nurtureReasons || []).length) params.nurture_reasons = filters.nurtureReasons.join(',')
      if (filters.country) params.country = filters.country
      if (filters.activity.length) params.activity = filters.activity.join(',')
      if (filters.manager) params.manager = filters.manager
      this.addRangeParams(params, 'request', filters.requestDates)
      this.addRangeParams(params, 'confirmation', filters.confirmationDates)
      this.addRangeParams(params, 'travel', filters.travelDates)
      if (filters.advSources.length) params.adv_sources = filters.advSources.join(',')
      if (filters.tourOperators.length) params.tour_operators = filters.tourOperators.join(',')
      if (filters.domains.length) params.domains = filters.domains.join(',')
      return params
    },
    addRangeParams(params, prefix, range) {
      if (!Array.isArray(range)) return
      if (range[0]) params[`${prefix}_from`] = range[0]
      if (range[1]) params[`${prefix}_to`] = range[1]
    },
    async loadSummary() {
      const requestId = ++this.summaryRequestId
      this.summaryLoading = true
      this.summaryError = ''
      try {
        const params = this.listParams()
        delete params.page
        delete params.page_size
        const { data } = await getLeadFlowTrackerLeadsSummary(params)
        if (requestId !== this.summaryRequestId) return
        this.summary = data
        this.domainOptions = data.domains || []
        this.nurtureReasonOptions = data.nurture_reasons || []
        this.scope = data.scope || this.scope
      } catch (error) {
        if (requestId !== this.summaryRequestId) return
        this.summaryError = this.errorMessage(error, 'Unable to load lead statistics')
      } finally {
        if (requestId === this.summaryRequestId) this.summaryLoading = false
      }
    },
    onFilterChange(filters, changedFilter) {
      let nextFilters = filters
      if (changedFilter === 'search' && (filters.search || '').trim()) {
        nextFilters = this.filtersFromQuery({ search: filters.search })
      }
      this.filters = this.cloneFilters(nextFilters)
      this.pagination.page = 1
      this.syncFilterRoute()
      window.clearTimeout(this.searchTimer)
      if (changedFilter === 'search') {
        this.listRequestId += 1
        this.listLoading = true
        this.listError = ''
        this.summaryRequestId += 1
        this.summaryLoading = true
        this.summaryError = ''
        this.searchTimer = window.setTimeout(this.loadResults, 400)
      } else {
        this.loadResults()
      }
    },
    toggleStageFilter(stageCode) {
      const stages = [...(this.filters.stages || [])]
      const selectedIndex = stages.indexOf(stageCode)
      if (selectedIndex === -1) stages.push(stageCode)
      else stages.splice(selectedIndex, 1)
      this.onFilterChange({ ...this.filters, stages }, 'stages')
    },
    clearStageFilters() {
      if (!(this.filters.stages || []).length) return
      this.onFilterChange({ ...this.filters, stages: [] }, 'stages')
    },
    clearFilters() {
      window.clearTimeout(this.searchTimer)
      this.filters = {
        search: '',
        stages: [],
        nurtureReasons: [],
        country: null,
        activity: [],
        manager: null,
        requestDates: [],
        confirmationDates: [],
        travelDates: [],
        advSources: [],
        tourOperators: [],
        domains: []
      }
      this.pagination.page = 1
      this.syncFilterRoute()
      this.loadResults()
    },
    hasAdvancedFilters(filters) {
      return Boolean(
        (filters.requestDates || []).length
        || (filters.confirmationDates || []).length
        || (filters.travelDates || []).length
        || (filters.advSources || []).length
        || (filters.tourOperators || []).length
        || (filters.domains || []).length
      )
    },
    withoutAdvancedFilters(filters) {
      return {
        ...filters,
        requestDates: [],
        confirmationDates: [],
        travelDates: [],
        advSources: [],
        tourOperators: [],
        domains: []
      }
    },
    async exportLeads() {
      if (this.exportLoading) return
      this.exportLoading = true
      try {
        const response = await getLeadFlowTrackerLeadsExport(this.filterParams())
        const url = window.URL.createObjectURL(new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }))
        const link = document.createElement('a')
        link.href = url
        link.download = `Lead Flow export ${new Date().toISOString().slice(0, 10)}.xlsx`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.$notify({
          type: 'error',
          title: this.errorMessage(error, 'Unable to export leads')
        })
      } finally {
        this.exportLoading = false
      }
    },
    changePage(page) {
      this.pagination.page = page
      this.loadLeads()
      this.$nextTick(() => {
        const table = this.$el.querySelector('.lead-flow-leads__table')
        if (table) table.scrollIntoView({ block: 'start' })
      })
    },
    openLead(payload) {
      const row = payload && payload.row ? payload.row : payload
      const event = payload && payload.event
      const trigger = event && event.target && event.target.closest('tr')
      if (row && row.id) this.openRouteModal(row.id, trigger)
    },
    async loadCountries() {
      try {
        const { data } = await getCountries()
        this.countryOptions = Array.isArray(data) ? data : []
      } catch (_) {
        this.countryOptions = []
      }
    },
    async loadFilterOptions() {
      const [advSources, tourOperators] = await Promise.allSettled([
        getLeadsAdvSources(),
        getClientsTourOperators()
      ])
      this.advSourceOptions = []
      if (advSources.status === 'fulfilled') {
        this.advSourceOptions = this.optionResults(advSources.value.data)
      }
      this.tourOperatorOptions = []
      if (tourOperators.status === 'fulfilled') {
        this.tourOperatorOptions = this.optionResults(tourOperators.value.data)
      }
    },
    optionResults(data) {
      if (Array.isArray(data)) return data
      return data && Array.isArray(data.results) ? data.results : []
    },
    onManagerOpen() {
      if (!this.managersLoaded && !this.managersLoading) this.loadManagers(true)
    },
    onManagerSearch(search) {
      this.managerSearch = (search || '').trim()
      window.clearTimeout(this.managerSearchTimer)
      const requestId = ++this.managerRequestId
      this.managerSearchTimer = window.setTimeout(() => this.loadManagers(true, requestId), 400)
    },
    async loadManagers(reset, scheduledRequestId) {
      if (!reset && (this.managersLoading || this.managersLoadingMore || !this.managerHasMore)) return
      const requestId = scheduledRequestId || (reset ? ++this.managerRequestId : this.managerRequestId)
      this.managerRequestId = requestId
      if (reset) {
        this.managersLoading = true
        this.managersLoadingMore = false
        this.managerNextCursor = null
        this.managerHasMore = false
        this.managerError = null
        this.managerOptions = this.selectedManagerOptions()
      } else {
        this.managersLoadingMore = true
        this.managerError = null
      }
      try {
        const params = { page_size: 50 }
        if (this.managerSearch) params.search = this.managerSearch
        if (!reset && this.managerNextCursor) params.cursor = this.managerNextCursor
        const { data } = await getLeadFlowTrackerManagers(params)
        if (requestId !== this.managerRequestId) return
        const current = reset ? this.selectedManagerOptions() : this.managerOptions
        this.managerOptions = this.deduplicateManagers(current.concat(data.results || []))
        this.managerNextCursor = data.next_cursor || null
        this.managerHasMore = Boolean(data.has_more)
        this.managersLoaded = true
      } catch (error) {
        if (requestId === this.managerRequestId) {
          this.managerError = error
          if (reset) this.managersLoaded = false
        }
      } finally {
        if (requestId === this.managerRequestId) {
          this.managersLoading = false
          this.managersLoadingMore = false
        }
      }
    },
    loadMoreManagers() {
      this.loadManagers(false)
    },
    retryManagers() {
      this.loadManagers(true)
    },
    selectedManagerOptions() {
      if (!this.filters.manager) return []
      const selected = this.managerOptions.find(manager => manager.id === this.filters.manager)
        || this.scope.selected_manager
      return selected && selected.id === this.filters.manager ? [selected] : []
    },
    addManagerOption(manager) {
      this.managerOptions = this.deduplicateManagers([manager].concat(this.managerOptions))
    },
    deduplicateManagers(managers) {
      const ids = new Set()
      return managers.filter(manager => {
        if (!manager || ids.has(manager.id)) return false
        ids.add(manager.id)
        return true
      })
    },
    normalizeManagerId(value) {
      const id = Number(value)
      return Number.isInteger(id) && id > 0 ? id : null
    },
    queryValue(value) {
      return Array.isArray(value) ? value[0] : value
    },
    csvQueryValues(value) {
      return String(this.queryValue(value) || '')
        .split(',')
        .map(item => item.trim())
        .filter(Boolean)
    },
    dateRangeFromQuery(query, prefix) {
      const from = this.queryValue(query[`${prefix}_from`]) || ''
      const to = this.queryValue(query[`${prefix}_to`]) || ''
      return from || to ? [from, to] : []
    },
    filtersFromQuery(query = {}) {
      return {
        search: String(this.queryValue(query.search) || ''),
        stages: this.csvQueryValues(query.stages),
        nurtureReasons: this.csvQueryValues(query.nurture_reasons),
        country: this.queryValue(query.country) || null,
        activity: this.csvQueryValues(query.activity),
        manager: this.normalizeManagerId(this.queryValue(query.manager)),
        requestDates: this.dateRangeFromQuery(query, 'request'),
        confirmationDates: this.dateRangeFromQuery(query, 'confirmation'),
        travelDates: this.dateRangeFromQuery(query, 'travel'),
        advSources: this.csvQueryValues(query.adv_sources),
        tourOperators: this.csvQueryValues(query.tour_operators).map(value => this.numericQueryValue(value)),
        domains: this.csvQueryValues(query.domains).map(value => this.numericQueryValue(value))
      }
    },
    numericQueryValue(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : value
    },
    cloneFilters(filters) {
      return {
        ...filters,
        stages: [...(filters.stages || [])],
        nurtureReasons: [...(filters.nurtureReasons || [])],
        activity: [...(filters.activity || [])],
        requestDates: [...(filters.requestDates || [])],
        confirmationDates: [...(filters.confirmationDates || [])],
        travelDates: [...(filters.travelDates || [])],
        advSources: [...(filters.advSources || [])],
        tourOperators: [...(filters.tourOperators || [])],
        domains: [...(filters.domains || [])]
      }
    },
    filterSignature(filters) {
      return JSON.stringify(this.filterParams(filters))
    },
    syncFilterRoute() {
      const query = { ...this.$route.query }
      const keys = [
        'search', 'stages', 'nurture_reasons', 'country', 'activity', 'manager',
        'request_from', 'request_to', 'confirmation_from', 'confirmation_to',
        'travel_from', 'travel_to', 'adv_sources', 'tour_operators', 'domains'
      ]
      keys.forEach(key => delete query[key])
      Object.entries(this.filterParams()).forEach(([key, value]) => {
        query[key] = String(value)
      })
      if (JSON.stringify(query) === JSON.stringify(this.$route.query)) return
      this.$router.replace({ query }).catch(() => {})
    },
    countryCode(country) {
      const code = country && country.code ? String(country.code) : ''
      return /^[a-z]{2}$/i.test(code) ? code : ''
    },
    countryName(country) {
      return country && country.name ? country.name : 'Country not provided'
    },
    hasParty(party = {}) {
      return [party.total, party.adults, party.teenagers, party.children].some(value => Number.isFinite(value))
    },
    partyTotal(party = {}) {
      if (Number.isFinite(party.total)) return party.total
      const total = [party.adults, party.teenagers, party.children]
        .reduce((sum, value) => sum + (Number.isFinite(value) ? value : 0), 0)
      return total || '—'
    },
    partyGroups(party = {}) {
      return [
        { code: 'adults', label: 'adults', count: party.adults, icon: AdultIcon },
        { code: 'teenagers', label: 'teenagers', count: party.teenagers, icon: TeenagerIcon },
        { code: 'children', label: 'children', count: party.children, icon: ChildIcon }
      ].filter(group => Number.isFinite(group.count) && group.count > 0)
    },
    partyLabel(party = {}) {
      const groups = this.partyGroups(party).map(group => `${group.count} ${group.label}`)
      return `${this.partyTotal(party)} pax${groups.length ? `: ${groups.join(', ')}` : ''}`
    },
    communicationChannels(contact = {}) {
      const preferred = String(contact.preferred_method || '').toLowerCase()
      return [
        {
          code: 'email',
          name: 'Email',
          available: Boolean(contact.has_email),
          preferred: preferred.includes('email')
        },
        {
          code: 'whatsapp',
          name: 'WhatsApp',
          icon: WhatsAppIcon,
          available: Boolean(contact.has_whatsapp),
          preferred: preferred.includes('whatsapp') || preferred.includes('whats app')
        }
      ].map(channel => ({
        ...channel,
        tooltip: `${channel.name}${channel.available ? '' : ' not provided'}${channel.preferred ? ' · Preferred' : ''}`
      }))
    },
    travelLabel(travel = {}) {
      return formatTravelMonth(travel.date)
        || (travel.planning_period && travel.planning_period.name)
        || ''
    },
    newTooltip(content) {
      return { content, placement: 'top', offset: 8, classes: 'tooltip--new' }
    },
    errorMessage(error, fallback) {
      const data = error.response && error.response.data
      if (data && typeof data.details === 'string') return data.details
      if (data && typeof data.detail === 'string') return data.detail
      return fallback
    }
  }
}
</script>

<style lang="scss" src="./lead-flow-theme.scss"></style>
